import React, { useState, useEffect, useCallback } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { usePortalAuth } from '../../contexts/PortalAuthContext';
import portalAPI, { portalDashboardAPI, taxDocumentsAPI } from '../../services/portalAPI';
import PortalFooter from '../../components/Layout/PortalFooter';
import DeliveryWarningBanner from '../../components/DeliveryWarningBanner';
import InstallAppBanner from '../../components/InstallAppBanner';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import LiveIndicator from '../../components/LiveIndicator';
import VoiceOrderAssistant from '../../components/Portal/VoiceOrderAssistant';
import { TutorialAutoStart, TutorialHelpButton } from '../../components/Portal/Tutorial';
// Farmhand surfaces (FarmHealthDashboard, FarmhandPromoCard, QuickVoiceLog)
// are hidden on the dashboard while we rebuild as Farmer's Partner (Feb 2027).
// The component files are preserved for the rebuild.
import '../../styles/portal-brand.css';

interface DashboardStats {
  open_orders: number;
  pending_quotes: number;
}

interface Order {
  id: number;
  order_number: string;
  order_date: string;
  delivery_date: string;
  status: string;
  status_display: string;
  total_amount: string;
}

interface Quote {
  id: number;
  quote_number: string;
  quote_date: string;
  expiration_date: string;
  days_until_expiration: number;
  status: string;
  status_display: string;
  total_amount: string;
}

interface DashboardData {
  stats: DashboardStats;
  recent_orders: Order[];
  pending_quotes: Quote[];
}

interface TaxDocumentStatus {
  has_approved_document: boolean;
  has_pending_document: boolean;
  expiring_soon: any[];
  expired: any[];
  needs_attention: boolean;
}

const PortalDashboard: React.FC = () => {
  const { user } = usePortalAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [taxDocStatus, setTaxDocStatus] = useState<TaxDocumentStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showVoiceOrder, setShowVoiceOrder] = useState(false);
  const [creditBalance, setCreditBalance] = useState(0);

  const loadDashboardData = useCallback(async () => {
    try {
      if (!data) setLoading(true);
      const response = await portalDashboardAPI.getStats();
      setData(response.data);
      setError('');
    } catch (err: any) {
      if (!data) setError('Failed to load dashboard data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [data]);

  const loadTaxDocumentStatus = async () => {
    try {
      const response = await taxDocumentsAPI.getStatus();
      setTaxDocStatus(response.data);
    } catch (err: any) {
      // Silently fail - tax documents may not be set up
      console.error('Failed to load tax document status:', err);
    }
  };

  const loadCreditBalance = async () => {
    try {
      const response = await portalAPI.get('/portal/credits/');
      const bal = parseFloat(response.data?.customer_credit?.balance || '0');
      setCreditBalance(Number.isNaN(bal) ? 0 : bal);
    } catch (err) {
      // Silently fail — credits are optional.
      setCreditBalance(0);
    }
  };

  useEffect(() => {
    loadDashboardData();
    loadTaxDocumentStatus();
    loadCreditBalance();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh every 30 seconds (smart - respects user activity)
  const { isRefreshing, secondsSinceUpdate, isPaused, pauseReason } = useAutoRefresh(loadDashboardData, {
    intervalMs: 30000,
    enabled: !loading && !error,
  });

  // Redirect crew members to their simplified dashboard
  if (user?.permissions?.farm_role === 'crew') {
    return <Navigate to="/portal/crew-dashboard" replace />;
  }

  // Farmhand is paused while we rebuild as Farmer's Partner (Feb 2027).
  // Activated farm customers used to land on the live Farm Health Dashboard
  // here; that surface is hidden, so all wholesale customers now see the
  // standard portal dashboard. The "Farmhand" nav item points to the
  // coming-soon page. (FarmHealthDashboard is preserved in code for the
  // rebuild.)

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Tutorial auto-start for first-time visitors */}
      <TutorialAutoStart pageKey="dashboard" />

      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Welcome, {user?.name || 'Customer'}</h1>
          <p style={styles.subtitle}>{user?.customer.name}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <TutorialHelpButton pageKey="dashboard" />
          <LiveIndicator
            isRefreshing={isRefreshing}
            secondsSinceUpdate={secondsSinceUpdate}
            isPaused={isPaused}
            pauseReason={pauseReason}
          />
        </div>
      </div>

      {/* Account credit banner — shows available prepayment/credit balance */}
      {creditBalance > 0 && (
        <Link
          to="/portal/billing"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12, flexWrap: 'wrap', textDecoration: 'none',
            background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 12,
            padding: '14px 18px', marginBottom: 16,
          }}
        >
          <span style={{ color: '#065f46', fontWeight: 600 }}>
            💳 Account Credit: {creditBalance.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
          </span>
          <span style={{ color: '#059669', fontWeight: 500, fontSize: 14 }}>
            Automatically applied to your invoices →
          </span>
        </Link>
      )}

      {/* Tax Exemption Warning Banner - shows when certificate is expired/expiring */}
      {user?.customer?.tax_exemption_status?.warning && (
        <div style={styles.taxWarningBanner}>
          <div style={styles.taxWarningContent}>
            <span style={styles.taxWarningIcon}>⚠️</span>
            <div style={styles.taxWarningText}>
              <strong>Tax Exemption Notice:</strong> {user.customer.tax_exemption_status.warning}
            </div>
            <Link to="/portal/tax-exemption" style={styles.taxWarningLink}>
              Upload Certificate →
            </Link>
          </div>
        </div>
      )}

      {/* Tax Document Status Banner */}
      {taxDocStatus?.needs_attention && !user?.customer?.tax_exemption_status?.warning && (
        <div style={styles.taxBanner}>
          <div style={styles.taxBannerContent}>
            <span style={styles.taxBannerIcon}>📄</span>
            <div style={styles.taxBannerText}>
              {!taxDocStatus.has_approved_document && !taxDocStatus.has_pending_document && (
                <span>Please upload your tax exemption certificate to ensure tax-exempt pricing on your orders.</span>
              )}
              {taxDocStatus.has_pending_document && (
                <span>Your tax exemption document is pending review. We'll notify you once it's approved.</span>
              )}
              {taxDocStatus.expired && taxDocStatus.expired.length > 0 && (
                <span>Your tax exemption certificate has expired. Please upload a new document to maintain tax-exempt status.</span>
              )}
              {taxDocStatus.expiring_soon && taxDocStatus.expiring_soon.length > 0 && !taxDocStatus.expired?.length && (
                <span>Your tax exemption certificate expires soon. Please upload an updated document.</span>
              )}
            </div>
            <Link to="/portal/tax-exemption" style={styles.taxBannerLink}>
              Manage Documents →
            </Link>
          </div>
        </div>
      )}

      {/* Delivery Information Banner */}
      {user?.customer?.id && (
        <DeliveryWarningBanner
          customerId={user.customer.id}
          style={{ marginBottom: '2rem' }}
        />
      )}

      {/* Stats Cards */}
      <div style={styles.statsGrid} data-tutorial="dashboard-stats">
        <div style={styles.statCard}>
          <div style={styles.statValue}>{data?.stats.open_orders || 0}</div>
          <div style={styles.statLabel}>Open Orders</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{data?.stats.pending_quotes || 0}</div>
          <div style={styles.statLabel}>Pending Quotes</div>
        </div>
      </div>

      {/* Voice Actions Section */}
      <div style={styles.voiceActionsSection}>
        {/* Quick Voice Log hidden while Farmhand is paused for the Farmer's
            Partner rebuild (Feb 2027). Component preserved for the rebuild. */}
        {/* Voice Order - only for retail partners who can place orders */}
        {user?.permissions.can_place_orders && user?.customer?.customer_type === 'RETAIL_PARTNER' && (
          <div style={styles.voiceActionCard}>
            <button
              onClick={() => setShowVoiceOrder(true)}
              style={styles.voiceOrderButton}
            >
              <span style={styles.voiceOrderIcon}>🎤</span>
              <span style={styles.voiceOrderText}>Order Feed by Voice</span>
            </button>
            <p style={styles.voiceLogHint}>50 bags = FREE shipping!</p>
          </div>
        )}
      </div>

      {/* Voice Order Assistant Modal */}
      {showVoiceOrder && (
        <VoiceOrderAssistant
          onClose={() => setShowVoiceOrder(false)}
          onSuccess={() => {
            setShowVoiceOrder(false);
            loadDashboardData();
          }}
        />
      )}

      {/* Quick Actions */}
      {user?.permissions.can_place_orders && (
        <div style={styles.section} data-tutorial="quick-actions">
          <h2 style={styles.sectionTitle}>Quick Actions</h2>
          <div style={styles.actionsGrid}>
            <Link to="/portal/place-order" style={styles.actionCard}>
              <div style={styles.actionIcon}>📝</div>
              <div style={styles.actionTitle}>Place New Order</div>
              <div style={styles.actionDescription}>Create a new order for delivery</div>
            </Link>
            <Link to="/portal/orders" style={styles.actionCard}>
              <div style={styles.actionIcon}>📦</div>
              <div style={styles.actionTitle}>View My Orders</div>
              <div style={styles.actionDescription}>Track and manage your orders</div>
            </Link>
            <Link to="/portal/quotes" style={styles.actionCard}>
              <div style={styles.actionIcon}>💰</div>
              <div style={styles.actionTitle}>View Quotes</div>
              <div style={styles.actionDescription}>Review and accept quotes</div>
            </Link>
          </div>
        </div>
      )}

      {/* Recent Orders */}
      {data?.recent_orders && Array.isArray(data.recent_orders) && data.recent_orders.length > 0 && (
        <div style={styles.section} data-tutorial="recent-orders">
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Recent Orders</h2>
            <Link to="/portal/orders" style={styles.viewAllLink}>View All →</Link>
          </div>
          {/* Desktop Table */}
          <div className="portal-table-desktop" style={styles.table}>
            <table style={styles.tableElement}>
              <thead>
                <tr>
                  <th style={styles.th}>Order #</th>
                  <th style={styles.th}>Order Date</th>
                  <th style={styles.th}>Delivery Date</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Total</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_orders.map((order) => (
                  <tr key={order.id}>
                    <td style={styles.td}>
                      <Link to={`/portal/orders/${order.id}`} style={styles.link}>
                        {order.order_number}
                      </Link>
                    </td>
                    <td style={styles.td}>{new Date(order.order_date).toLocaleDateString()}</td>
                    <td style={styles.td}>{new Date(order.delivery_date).toLocaleDateString()}</td>
                    <td style={styles.td}>
                      <span style={getStatusStyle(order.status)}>{order.status_display}</span>
                    </td>
                    <td style={styles.td}>${parseFloat(order.total_amount).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Mobile Cards */}
          <div className="portal-cards-mobile">
            {data.recent_orders.map((order) => (
              <Link key={order.id} to={`/portal/orders/${order.id}`} style={{ textDecoration: 'none' }}>
                <div className="portal-mobile-card">
                  <div className="portal-mobile-card-header">
                    <span className="portal-mobile-card-title">{order.order_number}</span>
                    <span style={getStatusStyle(order.status)}>{order.status_display}</span>
                  </div>
                  <div className="portal-mobile-card-row">
                    <span className="portal-mobile-card-label">Delivery</span>
                    <span className="portal-mobile-card-value">{new Date(order.delivery_date).toLocaleDateString()}</span>
                  </div>
                  <div className="portal-mobile-card-row">
                    <span className="portal-mobile-card-label">Total</span>
                    <span className="portal-mobile-card-value" style={{ fontWeight: 'bold', color: '#E73C3E' }}>${parseFloat(order.total_amount).toFixed(2)}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Pending Quotes */}
      {data?.pending_quotes && Array.isArray(data.pending_quotes) && data.pending_quotes.length > 0 && (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Pending Quotes</h2>
            <Link to="/portal/quotes" style={styles.viewAllLink}>View All →</Link>
          </div>
          {/* Desktop Table */}
          <div className="portal-table-desktop" style={styles.table}>
            <table style={styles.tableElement}>
              <thead>
                <tr>
                  <th style={styles.th}>Quote #</th>
                  <th style={styles.th}>Quote Date</th>
                  <th style={styles.th}>Expires</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Total</th>
                </tr>
              </thead>
              <tbody>
                {data.pending_quotes.map((quote) => (
                  <tr key={quote.id}>
                    <td style={styles.td}>
                      <Link to={`/portal/quotes/${quote.id}`} style={styles.link}>
                        {quote.quote_number}
                      </Link>
                    </td>
                    <td style={styles.td}>{new Date(quote.quote_date).toLocaleDateString()}</td>
                    <td style={styles.td}>
                      {quote.days_until_expiration > 0
                        ? `${quote.days_until_expiration} days`
                        : 'Expired'}
                    </td>
                    <td style={styles.td}>
                      <span style={getStatusStyle(quote.status)}>{quote.status_display}</span>
                    </td>
                    <td style={styles.td}>${parseFloat(quote.total_amount).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Mobile Cards */}
          <div className="portal-cards-mobile">
            {data.pending_quotes.map((quote) => (
              <Link key={quote.id} to={`/portal/quotes/${quote.id}`} style={{ textDecoration: 'none' }}>
                <div className="portal-mobile-card">
                  <div className="portal-mobile-card-header">
                    <span className="portal-mobile-card-title">{quote.quote_number}</span>
                    <span style={getStatusStyle(quote.status)}>{quote.status_display}</span>
                  </div>
                  <div className="portal-mobile-card-row">
                    <span className="portal-mobile-card-label">Expires</span>
                    <span className="portal-mobile-card-value">
                      {quote.days_until_expiration > 0 ? `${quote.days_until_expiration} days` : 'Expired'}
                    </span>
                  </div>
                  <div className="portal-mobile-card-row">
                    <span className="portal-mobile-card-label">Total</span>
                    <span className="portal-mobile-card-value" style={{ fontWeight: 'bold', color: '#E73C3E' }}>${parseFloat(quote.total_amount).toFixed(2)}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <PortalFooter />
      <InstallAppBanner />
    </div>
  );
};

const getStatusStyle = (status: string): React.CSSProperties => {
  const baseStyle: React.CSSProperties = {
    padding: '0.35rem 0.75rem',
    borderRadius: '12px',
    fontSize: '0.875rem',
    fontWeight: '500',
    display: 'inline-block',
  };

  switch (status) {
    case 'PENDING':
      return { ...baseStyle, backgroundColor: '#fef3c7', color: '#92400e' };
    case 'CONFIRMED':
      return { ...baseStyle, backgroundColor: '#dbeafe', color: '#1e40af' };
    case 'IN_PRODUCTION':
      return { ...baseStyle, backgroundColor: '#e0e7ff', color: '#3730a3' };
    case 'COMPLETED':
    case 'DELIVERED':
      return { ...baseStyle, backgroundColor: '#d1fae5', color: '#065f46' };
    default:
      return { ...baseStyle, backgroundColor: 'rgba(195, 173, 139, 0.2)', color: '#3F2021', border: '1px solid #C3AD8B' };
  }
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '1rem',
    backgroundColor: '#FAF9F6',
    minHeight: '100vh',
  },
  header: {
    marginBottom: '1.5rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '1rem',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#3F2021',
    margin: '0 0 0.5rem 0',
  },
  subtitle: {
    fontSize: '1rem',
    color: '#508590',
    margin: 0,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1rem',
    marginBottom: '1.5rem',
  },
  statCard: {
    backgroundColor: '#FFFFFF',
    padding: '1rem',
    borderRadius: '8px',
    border: '2px solid #C3AD8B',
    boxShadow: '0 2px 4px rgba(63, 32, 33, 0.1)',
    textAlign: 'center',
    transition: 'all 0.2s ease',
  },
  statValue: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#E73C3E',
    marginBottom: '0.5rem',
  },
  statLabel: {
    fontSize: '0.75rem',
    color: '#3F2021',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  section: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    border: '2px solid #C3AD8B',
    boxShadow: '0 2px 4px rgba(63, 32, 33, 0.1)',
    padding: '1rem',
    marginBottom: '1rem',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  sectionTitle: {
    fontSize: '1.125rem',
    fontWeight: 'bold',
    color: '#3F2021',
    margin: 0,
  },
  viewAllLink: {
    color: '#E73C3E',
    textDecoration: 'none',
    fontSize: '0.95rem',
    fontWeight: '500',
  },
  actionsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '1rem',
  },
  actionCard: {
    display: 'block',
    padding: '1rem',
    border: '2px solid #C3AD8B',
    borderRadius: '8px',
    textDecoration: 'none',
    transition: 'all 0.2s',
    cursor: 'pointer',
    backgroundColor: '#FFFFFF',
    minHeight: '44px',
  },
  actionIcon: {
    fontSize: '2rem',
    marginBottom: '0.5rem',
  },
  actionTitle: {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#3F2021',
    marginBottom: '0.25rem',
  },
  actionDescription: {
    fontSize: '0.8rem',
    color: '#508590',
  },
  table: {
    overflowX: 'auto',
  },
  tableElement: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: '#FFFFFF',
  },
  th: {
    textAlign: 'left',
    padding: '0.5rem',
    borderBottom: '2px solid #C3AD8B',
    fontSize: '0.7rem',
    fontWeight: '600',
    color: '#3F2021',
    textTransform: 'uppercase',
    backgroundColor: '#FAF9F6',
  },
  td: {
    padding: '0.5rem',
    borderBottom: '1px solid #C3AD8B',
    fontSize: '0.75rem',
    color: '#3F2021',
  },
  link: {
    color: '#E73C3E',
    textDecoration: 'none',
    fontWeight: '500',
  },
  loading: {
    textAlign: 'center',
    padding: '3rem',
    fontSize: '1.125rem',
    color: '#508590',
  },
  error: {
    backgroundColor: '#fee2e2',
    border: '1px solid #fca5a5',
    color: '#991b1b',
    padding: '1rem',
    borderRadius: '4px',
    textAlign: 'center',
  },
  // Tax Exemption Warning Banner (more prominent for expired/expiring certs)
  taxWarningBanner: {
    backgroundColor: '#fee2e2',
    border: '2px solid #ef4444',
    borderRadius: '8px',
    padding: '1rem',
    marginBottom: '1.5rem',
  },
  taxWarningContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap' as const,
  },
  taxWarningIcon: {
    fontSize: '1.5rem',
  },
  taxWarningText: {
    flex: 1,
    color: '#991b1b',
    fontSize: '0.9rem',
    lineHeight: '1.4',
    minWidth: '200px',
  },
  taxWarningLink: {
    backgroundColor: '#dc2626',
    color: 'white',
    padding: '0.625rem 1rem',
    borderRadius: '4px',
    textDecoration: 'none',
    fontWeight: '600',
    fontSize: '0.85rem',
    whiteSpace: 'nowrap' as const,
    minHeight: '44px',
    display: 'inline-flex',
    alignItems: 'center',
  },
  // Tax Document Status Banner (info level)
  taxBanner: {
    backgroundColor: '#fff3cd',
    border: '2px solid #ffc107',
    borderRadius: '8px',
    padding: '1rem',
    marginBottom: '1.5rem',
  },
  taxBannerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap' as const,
  },
  taxBannerIcon: {
    fontSize: '1.25rem',
  },
  taxBannerText: {
    flex: 1,
    color: '#856404',
    fontSize: '0.85rem',
    lineHeight: '1.4',
    minWidth: '200px',
  },
  taxBannerLink: {
    backgroundColor: '#E73C3E',
    color: 'white',
    padding: '0.625rem 1rem',
    borderRadius: '4px',
    textDecoration: 'none',
    fontWeight: '600',
    fontSize: '0.8rem',
    whiteSpace: 'nowrap' as const,
    minHeight: '44px',
    display: 'inline-flex',
    alignItems: 'center',
  },
  voiceActionsSection: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1rem',
    marginBottom: '1.5rem',
  },
  voiceActionCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    border: '2px solid #C3AD8B',
    boxShadow: '0 2px 4px rgba(63, 32, 33, 0.1)',
    padding: '1.5rem',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '12px',
  },
  voiceOrderButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px 24px',
    backgroundColor: '#E73C3E',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '30px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '600',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 12px rgba(231, 60, 62, 0.3)',
    minWidth: '200px',
    justifyContent: 'center',
  },
  voiceOrderIcon: {
    fontSize: '1.5rem',
  },
  voiceOrderText: {
    whiteSpace: 'nowrap' as const,
  },
  voiceLogHint: {
    margin: 0,
    fontSize: '0.85rem',
    color: '#6b7280',
    textAlign: 'center' as const,
  },
};

export default PortalDashboard;
